// A minimal WebGL2 sprite renderer: instanced quads from the sprite sheet, one buffer upload and one draw per blend
// group. Its canvas lies over the map (pointer-events: none); the map and deck.gl keep their own contexts.

import { CELL, drawSprites, GRID } from "./sprites";

/** Floats per sprite instance: x, y (px), half width, half height (px), rotation, sprite cell, r, g, b, a. */
export const STRIDE = 10;

const VS = `#version 300 es
layout(location=0) in vec2 aCorner;
layout(location=1) in vec4 iPos;   // x, y, half width, half height (css px)
layout(location=2) in vec2 iRot;   // rotation (rad), sprite cell
layout(location=3) in vec4 iColor; // straight rgb, alpha
uniform vec2 uView;
out vec2 vUv;
out vec4 vColor;
void main() {
  float c = cos(iRot.x), s = sin(iRot.x);
  vec2 p = aCorner * iPos.zw;
  p = vec2(p.x * c - p.y * s, p.x * s + p.y * c) + iPos.xy;
  gl_Position = vec4(p.x / uView.x * 2.0 - 1.0, 1.0 - p.y / uView.y * 2.0, 0.0, 1.0);
  float cell = iRot.y;
  vec2 xy = vec2(mod(cell, ${GRID}.0), floor(cell / ${GRID}.0));
  // half a texel inset keeps neighbouring cells from bleeding in
  vec2 uv = aCorner * 0.5 + 0.5;
  uv = mix(vec2(0.5 / ${CELL}.0), vec2(1.0 - 0.5 / ${CELL}.0), uv);
  vUv = (xy + uv) / ${GRID}.0;
  vColor = iColor;
}`;

const FS = `#version 300 es
precision mediump float;
in vec2 vUv;
in vec4 vColor;
uniform sampler2D uTex;
uniform float uAdd;
out vec4 frag;
void main() {
  vec4 t = texture(uTex, vUv);          // premultiplied
  vec3 rgb = vColor.rgb * vColor.a * t.rgb;
  float a = vColor.a * t.a;
  // additive light: alpha follows brightness, so the canvas stays valid premultiplied over the map
  frag = uAdd > 0.5 ? vec4(rgb, max(rgb.r, max(rgb.g, rgb.b))) : vec4(rgb, a);
}`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(`fx shader: ${gl.getShaderInfoLog(sh)}`);
  return sh;
}

interface Gpu {
  prog: WebGLProgram;
  shaders: WebGLShader[];
  vao: WebGLVertexArrayObject;
  quad: WebGLBuffer;
  inst: WebGLBuffer;
  tex: WebGLTexture;
  uView: WebGLUniformLocation;
  uAdd: WebGLUniformLocation;
}

export class SpriteRenderer {
  readonly gl: WebGL2RenderingContext;
  /** True when the GPU is emulated in software (SwiftShader, llvmpipe): draw less. */
  readonly software: boolean;
  private gpu: Gpu;
  private capacity = 0;
  private w = 1;
  private h = 1;
  lost = false;
  private onLost = (e: Event) => { e.preventDefault(); this.lost = true; };
  // a restored context has none of the old resources: build them again
  private onRestored = () => { this.gpu = this.build(); this.capacity = 0; this.lost = false; };

  constructor(readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl2", { premultipliedAlpha: true, alpha: true, antialias: false, depth: false, stencil: false });
    if (!gl) throw new Error("WebGL2 unavailable");
    this.gl = gl;
    // Chrome masks RENDERER ("WebKit WebGL") and needs the debug extension; Firefox answers directly
    let renderer = String(gl.getParameter(gl.RENDERER));
    if (/^webkit webgl$/i.test(renderer)) {
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      if (dbg) renderer = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL));
    }
    this.software = /swiftshader|llvmpipe|software|softpipe/i.test(renderer);
    this.gpu = this.build();
    canvas.addEventListener("webglcontextlost", this.onLost);
    canvas.addEventListener("webglcontextrestored", this.onRestored);
  }

  private build(): Gpu {
    const gl = this.gl;
    const shaders = [compile(gl, gl.VERTEX_SHADER, VS), compile(gl, gl.FRAGMENT_SHADER, FS)];
    const prog = gl.createProgram()!;
    for (const sh of shaders) gl.attachShader(prog, sh);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(`fx program: ${gl.getProgramInfoLog(prog)}`);

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const quad = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 8, 0);
    const inst = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, inst);
    const B = STRIDE * 4;
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, B, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, B, 16);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, B, 24);
    gl.vertexAttribDivisor(3, 1);
    gl.bindVertexArray(null);

    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, drawSprites());
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return {
      prog, shaders, vao, quad, inst, tex,
      uView: gl.getUniformLocation(prog, "uView")!, uAdd: gl.getUniformLocation(prog, "uAdd")!,
    };
  }

  /** Size the drawing buffer: css size × scale (the effects are soft; they need no more than the screen's pixels). */
  resize(cssW: number, cssH: number, scale: number) {
    const W = Math.max(1, Math.round(cssW * scale)), H = Math.max(1, Math.round(cssH * scale));
    if (this.canvas.width !== W || this.canvas.height !== H) {
      this.canvas.width = W;
      this.canvas.height = H;
    }
    this.w = Math.max(1, cssW);
    this.h = Math.max(1, cssH);
  }

  begin() {
    if (this.lost) return;
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  /** Draw `count` instances from `data` (STRIDE floats each), blended normally or additively. */
  draw(data: Float32Array, count: number, additive: boolean) {
    if (!count || this.lost) return;
    const gl = this.gl, g = this.gpu;
    gl.useProgram(g.prog);
    gl.bindVertexArray(g.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, g.inst);
    const bytes = count * STRIDE * 4;
    if (bytes > this.capacity) {
      this.capacity = Math.max(bytes, this.capacity * 2, 64 * 1024);
      gl.bufferData(gl.ARRAY_BUFFER, this.capacity, gl.DYNAMIC_DRAW);
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, count * STRIDE);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, g.tex);
    gl.uniform2f(g.uView, this.w, this.h);
    gl.uniform1f(g.uAdd, additive ? 1 : 0);
    gl.enable(gl.BLEND);
    if (additive) gl.blendFuncSeparate(gl.ONE, gl.ONE, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    else gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
    gl.bindVertexArray(null);
  }

  /** Free the GPU resources. `release`: the canvas is going away, so give the context itself back too (browsers
   * keep only so many; the oldest, possibly the map's, would be dropped). */
  destroy(release = false) {
    const gl = this.gl, g = this.gpu;
    this.canvas.removeEventListener("webglcontextlost", this.onLost);
    this.canvas.removeEventListener("webglcontextrestored", this.onRestored);
    if (!gl.isContextLost()) {
      gl.deleteBuffer(g.inst);
      gl.deleteBuffer(g.quad);
      gl.deleteTexture(g.tex);
      gl.deleteVertexArray(g.vao);
      for (const sh of g.shaders) gl.deleteShader(sh);
      gl.deleteProgram(g.prog);
    }
    if (release) gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}
